/**
 * HTTP status from partner API responses (axios). Set by Connector so UI can prioritize e.g. 422.
 *
 * @param {object} payload
 * @returns {number|undefined}
 */
export function getPartnerApiHttpStatus(payload) {
  if (!payload || typeof payload !== "object") return undefined;
  const raw = payload.httpStatus;
  const n = typeof raw === "string" ? parseInt(raw, 10) : Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** 422 Unprocessable Entity — validation / semantic errors; handled before generic errorCode routing. */
export function isUnprocessableEntityError(payload) {
  return getPartnerApiHttpStatus(payload) === 422;
}

/**
 * HTTP 4xx client errors (400–499), including 422 — unauthorized-style error page chrome.
 *
 * @param {object} [payload] - Partner API / Connector error object (may include `httpStatus`)
 * @returns {boolean}
 */
export function isHttp400SeriesClientError(payload) {
  const n = getPartnerApiHttpStatus(payload);
  if (typeof n !== "number" || !Number.isFinite(n)) return false;
  return n >= 400 && n < 500;
}

/**
 * Normalizes an axios error into the partner-API error shape used by {@link AppErrorPage},
 * {@link normalizeApiErrorMessages}, and {@link toastPartnerApiFailure}.
 *
 * - With `error.response`: merges JSON body when possible, always sets `httpStatus` from the real status.
 * - Without `error.response` (network, timeout, etc.): minimal `{ isError, message }` — no axios `toJSON()`
 *   spread (avoids huge `config` blobs and wrong `httpStatus` from axios `code`).
 *
 * @param {*} error - Typically an axios error from `instance.request().catch`
 * @returns {{ isError: true, httpStatus?: number, message?: string, [key: string]: unknown }}
 */
export function buildErrorResponse(error) {
  if (error == null) {
    return { isError: true };
  }

  const response = error.response;
  if (response != null) {
    const status = response?.status;
    const data = response?.data;

    let body = {};
    if (data != null && typeof data === "object" && !Array.isArray(data)) {
      body = { ...data };
    } else if (typeof data === "string" && data.trim()) {
      body = { message: data.trim() };
    }

    const httpStatus =
      typeof status === "number" && Number.isFinite(status)
        ? status
        : undefined;

    return {
      isError: true,
      ...body,
      ...(httpStatus !== undefined ? { httpStatus } : {}),
    };
  }

  const msg =
    typeof error.message === "string" && error.message.trim()
      ? error.message.trim()
      : undefined;

  return {
    isError: true,
    ...(msg ? { message: msg } : {}),
  };
}
